import React, { JSX } from 'react';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';

// Utils
import { StockDetailsContent } from '../../../constants/Constants';
import { SearchableOption, searchableFilterSx } from './stockDetails.constants';

// Common Components
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

// Assets
import stationIcon from '../../../assets/images/station-icon.svg';

interface StockStationFilterProps {
  stationId: string;
  stationInputValue: string;
  options: SearchableOption[];
  loading: boolean;
  onInputValueChange: (value: string) => void;
  onSelect: (stationId: string, selectedOption: SearchableOption) => void;
}

export default function StockStationFilter({
  stationId,
  stationInputValue,
  options,
  loading,
  onInputValueChange,
  onSelect,
}: StockStationFilterProps): JSX.Element {
  return (
    <FormInputCollection
      label={StockDetailsContent.stationLabel}
      style={{
        marginRight: '1.5em',
        width: '200px',
        flexShrink: 0,
      }}
    >
      <Autocomplete<SearchableOption, false, true, false>
        id='station'
        options={options}
        // Controlled from the very first render: `options` arrive asynchronously, so no
        // option matches `stationId` yet. `null` is a controlled "nothing selected" --
        // `undefined` makes MUI treat the field as uncontrolled, and it then keeps its own
        // internal value and ignores this prop for the rest of its life. The cast is only
        // needed because `disableClearable` types the value as non-nullable.
        value={(options.find((option) => option.value === stationId) ?? null) as SearchableOption}
        inputValue={stationInputValue}
        loading={loading}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        getOptionLabel={(option) => option.label}
        onInputChange={(_, value, reason) => {
          if (reason === 'input') {
            onInputValueChange(value);
          }

          if (reason === 'clear') {
            onInputValueChange('');
          }
        }}
        onChange={(_, option) => {
          if (!option) {
            return;
          }

          onSelect(option.value, option);
        }}
        size='small'
        disableClearable
        sx={{
          ...searchableFilterSx,
          width: '200px',
        }}
        ListboxProps={{
          sx: {
            maxHeight: 320,

            '& .MuiAutocomplete-option': {
              minHeight: 40,
              fontSize: 12,
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={StockDetailsContent.stationLabel}
            sx={{
              '& .MuiOutlinedInput-root': {
                paddingLeft: '8px !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E2E8F0',
              },
            }}
            InputProps={{
              ...params.InputProps,

              startAdornment: (
                <InputAdornment position='start'>
                  <img src={stationIcon} alt='' aria-hidden='true' />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </FormInputCollection>
  );
}
